/**
 * The customer map's filter bar — the one place a user narrows what the map and
 * its sidebar show.
 *
 * It is the same chip-and-popover bar the site grid wears, on purpose: an
 * operator who has learned to narrow a list of sites should not have to learn a
 * second set of controls to narrow the same sites on a map, and the two offer
 * the same dimensions in the same words. The bar sits above the canvas rather
 * than over it, so it never covers a marker or the zoom control.
 *
 * Narrowing controls (group, status, alarms, load types) ride in the bar's
 * filter zone and answer to "clear all".
 *
 * Search sits in the bar beside the narrowing chips, in the site grid's own
 * search field, because it narrows the same set the chips do.
 *
 * The group picker asks the server for a page at a time: an operator with
 * thousands of plants should not wait on all of them to choose one. It offers
 * each group the way the group autocomplete does — name with the typed text
 * marked, description underneath — because the server matches on the
 * description too, and a hit the reader cannot see the reason for reads as a
 * wrong result.
 *
 * Every control reads and writes the map's URL state directly, so what the bar
 * shows and what a shared link reproduces can never disagree.
 */
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { fetchSiteGroupById, fetchSiteGroupPage } from '@sophocles/api/siteGroup';
import FilterBar from '@sophocles/components/FilterBar';
import HighlightedOptionRow from '@sophocles/components/HighlightedOptionRow';
import { DataGridSingleSelectFilter } from '@sophocles/data-grids/components/filters';
import { selectAlarmCategories, selectRelayLoadTypes } from '@sophocles/features/reference';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import { tAlarm as translateAlarm } from '@sophocles/utils/alarmTranslation';
import { buildAlarmCategoryOptions, countSitesByAlarmCategory } from '@sophocles/views/map/mapAlarmOptions';
import MapSearchField from '@sophocles/views/map/components/MapSearchField';
import { CLEARED_FILTERS, STATUS_META } from '@sophocles/views/map/mapFilters';

const GROUP_PAGE_SIZE = 25;

/** A site-group row as the picker's option: the id it filters by, its name, and
 * the description the server also matched against. */
function toGroupOption(group) {
  return {
    value: String(group.id),
    label: group.name || String(group.id),
    description: group.description || '',
  };
}

function renderGroupOption(option, searchText) {
  return (
    <HighlightedOptionRow
      label={option.label}
      description={option.description}
      searchText={searchText}
      primaryVariant="body2"
    />
  );
}
const DOT_SIZE = 10;

/** A status option label: the palette dot the map paints, then the word. */
function DottedLabel({ family, children }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: DOT_SIZE,
          height: DOT_SIZE,
          borderRadius: '50%',
          bgcolor: `var(--status-${family})`,
          flexShrink: 0,
        }}
      />
      <span>{children}</span>
    </Stack>
  );
}

/**
 * @param {object} props
 * @param {object} props.state - the parsed map URL state.
 * @param {(patch: object) => void} props.setState
 * @param {?(string|number)} props.customerId - whose groups the picker offers.
 * @param {boolean} [props.isBillboard] - read alarm names in the billboard
 *   rather than the commercial vocabulary.
 * @param {object[]} [props.sites] - the fetched site set, which is what the
 *   alarm picker counts; it offers only categories some site actually carries.
 * @param {number} props.matchCount - sites the current filters and search leave,
 *   which the search field reports in-field the way the grid's does.
 * @param {(text: string) => void} props.onSearchChange - commit a new search text.
 * @param {boolean} [props.showGroupFilter] - offer the group picker; off under a
 *   plant route, where the group is the page rather than a narrowing.
 */
export default function MapFilterBar({
  state,
  setState,
  customerId,
  isBillboard = false,
  sites = [],
  matchCount,
  onSearchChange,
  showGroupFilter = true,
}) {
  const { t } = useTranslation();
  const siteGroupTerm = useSiteGroupTerm();
  const allCategories = useSelector(selectAlarmCategories);
  const relayLoadTypes = useSelector(selectRelayLoadTypes);

  const setList = useCallback(key => value => setState({ [key]: value ?? [] }), [setState]);

  const fetchGroupPage = useCallback(
    ({ search, offset, limit, signal }) =>
      fetchSiteGroupPage({ customerId, search, offset, limit }, { signal }).then(({ rows, total }) => ({
        options: rows.map(toGroupOption),
        total,
      })),
    [customerId],
  );

  const resolveGroup = useCallback(
    (value, { signal }) => fetchSiteGroupById(value, { signal }).then(group => (group ? toGroupOption(group) : null)),
    [],
  );

  const statusOptions = useMemo(
    () =>
      STATUS_META.map(meta => ({
        value: meta.status,
        label: <DottedLabel family={meta.family}>{t(meta.label[0], meta.label[1])}</DottedLabel>,
      })),
    [t],
  );

  // Named the way the site grid names them: the category's own locale key read
  // through the alarm translator, so a commercial tenant sees its own wording.
  const categoryOptions = useMemo(() => {
    const translate = (localeKey, fallback) => translateAlarm(t, localeKey, isBillboard, { defaultValue: fallback });
    return buildAlarmCategoryOptions(allCategories, countSitesByAlarmCategory(sites), translate, state.cat);
  }, [allCategories, sites, t, isBillboard, state.cat]);

  const loadTypeOptions = useMemo(
    () => relayLoadTypes.map(loadType => ({ value: String(loadType.id), label: loadType.name })),
    [relayLoadTypes],
  );

  const quickFilters = useMemo(() => {
    const groupDef = {
      key: 'group',
      label: siteGroupTerm.singular,
      type: 'async',
      icon: <LayersOutlinedIcon sx={{ fontSize: 16 }} />,
      allLabel: t('map.allGroups', { groups: siteGroupTerm.plural, defaultValue: 'All {{groups}}' }),
      searchPlaceholder: t('map.searchGroups', {
        groups: siteGroupTerm.plural,
        defaultValue: 'Search {{groups}}',
      }),
      fetchPage: fetchGroupPage,
      resolveValue: resolveGroup,
      renderOption: renderGroupOption,
      value: state.group,
      onChange: value => setState({ group: value ?? null }),
    };

    const defs = [
      ...(showGroupFilter ? [groupDef] : []),
      {
        key: 'status',
        label: t('general.status', 'Status'),
        type: 'multi',
        icon: <RadioButtonCheckedIcon sx={{ fontSize: 16 }} />,
        options: statusOptions,
        value: state.status,
        onChange: setList('status'),
      },
      {
        key: 'cat',
        label: t('general.alarms', 'Alarms'),
        type: 'multi',
        icon: <NotificationsActiveOutlinedIcon sx={{ fontSize: 16 }} />,
        options: categoryOptions,
        value: state.cat,
        onChange: setList('cat'),
        disabled: categoryOptions.length === 0,
      },
    ];

    // Mirrors the site grid: the load-type filter only exists for a customer
    // whose reference data defines load types at all.
    if (loadTypeOptions.length > 0) {
      defs.push({
        key: 'lt',
        label: t('sites.loadTypes', 'Load Types'),
        type: 'multi',
        icon: <LightbulbOutlinedIcon sx={{ fontSize: 16 }} />,
        options: loadTypeOptions,
        value: state.lt,
        onChange: setList('lt'),
      });
    }

    return defs;
  }, [
    t,
    siteGroupTerm.singular,
    siteGroupTerm.plural,
    fetchGroupPage,
    resolveGroup,
    statusOptions,
    categoryOptions,
    loadTypeOptions,
    state.group,
    state.status,
    state.cat,
    state.lt,
    setState,
    setList,
    showGroupFilter,
  ]);

  const handleResetAll = useCallback(() => setState({ ...CLEARED_FILTERS }), [setState]);

  return (
    <FilterBar
      search={<MapSearchField value={state.q} onChange={onSearchChange} matchCount={matchCount} />}
      quickFilters={quickFilters}
      onResetAll={handleResetAll}
    />
  );
}
